import { Fragment, useState } from 'react';

import {
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import {
    CaregiverToolErrorCard,
    formatCaregiverSignalLabel,
    getStringArray,
    TOOL_SURFACE_HOVER_CLASS,
    ToolToken,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { Badge } from '@apps/web/components/ui/badge';
import { Markdown } from '@apps/web/components/ui/markdown';
import { cn } from '@apps/web/lib/utils';

export const CAREGIVER_MEMORY_CARD_LIMIT = 4;
const CAREGIVER_MEMORY_CHIP_LIMIT = 5;
function getCaregiverNameFromArgs(
    args: Record<string, unknown> | null,
    caregiverId: string | null,
): string | null {
    const caregiversArg = Array.isArray(args?.['caregivers'])
        ? args['caregivers']
        : [];
    for (const caregiver of caregiversArg) {
        if (!isRecord(caregiver)) {
            continue;
        }

        const candidateId = getStringValue(caregiver, 'caregiver_id');
        if (caregiverId !== null && candidateId !== caregiverId) {
            continue;
        }

        const caregiverName = getStringValue(caregiver, 'caregiver_name');
        if (caregiverName) {
            return caregiverName;
        }
    }

    return null;
}

type CaregiverSignalTone = 'match' | 'conflict' | 'neutral';

export type CaregiverSignalMatcher = {
    readonly matchTerms: ReadonlySet<string>;
    readonly conflictTerms: ReadonlySet<string>;
};

const EMPTY_CAREGIVER_SIGNAL_MATCHER: CaregiverSignalMatcher = {
    matchTerms: new Set<string>(),
    conflictTerms: new Set<string>(),
};

const CAREGIVER_MATCH_SKIP_KEYS = new Set([
    'caregiver_id',
    'caregiver_name',
    'id',
    'name',
]);

const CAREGIVER_CONFLICT_KEY_PATTERN =
    /(?:conflict|blocker|exclude|exclusion|negative|unavailable)/i;

const CAREGIVER_MATCH_SOURCE_KEYS = [
    'memory',
    'compiled_markdown',
    'external_profile_note',
    'agent_instructions',
    'notes',
    'fit_reason',
    'fit_reasons',
    'match_reason',
    'match_reasons',
    'recommendation_reason',
    'recommendation_reasons',
    'reason',
    'reasons',
    'rationale',
    'summary',
    'justification',
    'why',
];

export function CaregiverNameButton({
    caregiverId,
    name,
    onCaregiverClick,
    className,
}: {
    readonly caregiverId: string | null;
    readonly name: string;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly className?: string;
}) {
    if (!caregiverId || !onCaregiverClick) {
        return <span className={className}>{name}</span>;
    }

    return (
        <button
            type="button"
            onClick={(event) => {
                event.stopPropagation();
                onCaregiverClick(caregiverId);
            }}
            className={cn(
                'text-primary decoration-primary/40 hover:decoration-primary cursor-pointer text-left underline underline-offset-2',
                className,
            )}
        >
            {name}
        </button>
    );
}

export function CaregiverSignalChips({
    values,
    limit = CAREGIVER_MEMORY_CHIP_LIMIT,
    matcher = EMPTY_CAREGIVER_SIGNAL_MATCHER,
}: {
    readonly values: Array<string>;
    readonly limit?: number;
    readonly matcher?: CaregiverSignalMatcher;
}) {
    const visibleValues = values.slice(0, limit);
    if (visibleValues.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-wrap gap-1">
            {visibleValues.map((value) => {
                const tone = getCaregiverSignalTone(value, matcher);

                return (
                    <Badge
                        key={value}
                        variant="secondary"
                        data-match-state={tone}
                        className={cn(
                            'rounded-full px-1.5 py-0 text-[10px]',
                            tone === 'match'
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300'
                                : tone === 'conflict'
                                  ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300'
                                  : null,
                        )}
                    >
                        {formatCaregiverSignalLabel(value)}
                    </Badge>
                );
            })}
            {values.length > visibleValues.length ? (
                <Badge
                    variant="outline"
                    className="rounded-full px-1.5 py-0 text-[10px]"
                >
                    +{values.length - visibleValues.length}
                </Badge>
            ) : null}
        </div>
    );
}

export function CaregiverSignalEvidenceSection({
    values,
    matcher,
    className,
}: {
    readonly values: Array<string>;
    readonly matcher: CaregiverSignalMatcher;
    readonly className?: string;
}) {
    const groups = values.reduce(
        (accumulator, value) => {
            const tone = getCaregiverSignalTone(value, matcher);
            if (tone === 'match') {
                accumulator.matches.push(value);
            } else if (tone === 'conflict') {
                accumulator.conflicts.push(value);
            } else {
                accumulator.profile.push(value);
            }
            return accumulator;
        },
        {
            matches: [] as Array<string>,
            conflicts: [] as Array<string>,
            profile: [] as Array<string>,
        },
    );
    const rows = [
        ['Matches', groups.matches],
        ['Conflicts', groups.conflicts],
        ['Profile', groups.profile],
    ] as const;

    if (values.length === 0) {
        return null;
    }

    return (
        <ToolTopicSection title="Fit signals" className={className}>
            <div className="space-y-1.5">
                {rows.map(([label, rowValues]) =>
                    rowValues.length > 0 ? (
                        <div
                            key={label}
                            className="grid grid-cols-[4.5rem_1fr] items-start gap-2"
                        >
                            <span className="text-muted-foreground pt-0.5 text-[10px] font-medium">
                                {label}
                            </span>
                            <CaregiverSignalChips
                                values={rowValues}
                                limit={rowValues.length}
                                matcher={matcher}
                            />
                        </div>
                    ) : null,
                )}
            </div>
        </ToolTopicSection>
    );
}

export function buildCaregiverSignalMatcher(
    ...sources: Array<unknown>
): CaregiverSignalMatcher {
    const matchTerms = new Set<string>();
    const conflictTerms = new Set<string>();

    for (const source of sources) {
        collectCaregiverSignalTerms(source, matchTerms, conflictTerms);
    }

    if (matchTerms.size === 0 && conflictTerms.size === 0) {
        return EMPTY_CAREGIVER_SIGNAL_MATCHER;
    }

    return { matchTerms, conflictTerms };
}

export function getCaregiverMatchSources(
    caregiver: Record<string, unknown>,
): Array<unknown> {
    return CAREGIVER_MATCH_SOURCE_KEYS.map((key) => caregiver[key]).filter(
        (value) => value !== null && value !== undefined,
    );
}

export function uniqueCaregiverSignalValues(
    values: Array<string>,
): Array<string> {
    const seen = new Set<string>();
    const uniqueValues: Array<string> = [];
    for (const value of values) {
        const key = normalizeCaregiverSignal(value);
        if (!key || seen.has(key)) {
            continue;
        }
        seen.add(key);
        uniqueValues.push(value);
    }
    return uniqueValues;
}

function collectCaregiverSignalTerms(
    value: unknown,
    matchTerms: Set<string>,
    conflictTerms: Set<string>,
    parentKey = '',
) {
    if (typeof value === 'string') {
        addCaregiverSignalTerms(
            value,
            CAREGIVER_CONFLICT_KEY_PATTERN.test(parentKey)
                ? conflictTerms
                : matchTerms,
        );
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((item) =>
            collectCaregiverSignalTerms(
                item,
                matchTerms,
                conflictTerms,
                parentKey,
            ),
        );
        return;
    }

    if (!isRecord(value)) {
        return;
    }

    for (const [key, childValue] of Object.entries(value)) {
        if (CAREGIVER_MATCH_SKIP_KEYS.has(key)) {
            continue;
        }

        collectCaregiverSignalTerms(childValue, matchTerms, conflictTerms, key);
    }
}

function addCaregiverSignalTerms(value: string, terms: Set<string>) {
    const normalized = normalizeCaregiverSignal(value);
    if (!normalized) {
        return;
    }

    terms.add(normalized);
    for (const word of normalized.split(/\s+/)) {
        if (word.length >= 2) {
            terms.add(normalizeCaregiverSignalWord(word));
        }
    }
}

function getCaregiverSignalTone(
    value: string,
    matcher: CaregiverSignalMatcher,
): CaregiverSignalTone {
    const normalized = normalizeCaregiverSignal(value);
    if (!normalized) {
        return 'neutral';
    }

    if (caregiverSignalMatches(normalized, matcher.conflictTerms)) {
        return 'conflict';
    }

    if (caregiverSignalMatches(normalized, matcher.matchTerms)) {
        return 'match';
    }

    return 'neutral';
}

function caregiverSignalMatches(
    normalizedValue: string,
    terms: ReadonlySet<string>,
): boolean {
    const normalizedValueWords = normalizedValue
        .split(/\s+/)
        .map(normalizeCaregiverSignalWord);

    for (const term of terms) {
        if (
            normalizedValue === term ||
            normalizedValue.includes(term) ||
            term.includes(normalizedValue) ||
            normalizedValueWords.includes(term)
        ) {
            return true;
        }
    }

    return false;
}

function normalizeCaregiverSignal(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}

function normalizeCaregiverSignalWord(value: string): string {
    return value.length > 3 && value.endsWith('s') ? value.slice(0, -1) : value;
}

export function CaregiverMemorySignalSection({
    label,
    values,
    matcher,
}: {
    readonly label: string;
    readonly values: Array<string>;
    readonly matcher: CaregiverSignalMatcher;
}) {
    if (values.length === 0) {
        return null;
    }

    return (
        <ToolTopicSection title={label}>
            <CaregiverSignalChips values={values} matcher={matcher} />
        </ToolTopicSection>
    );
}

function CaregiverProfileContextSection({
    caregiver,
}: {
    readonly caregiver: Record<string, unknown>;
}) {
    const contextItems = [
        {
            label: 'Profile note',
            value: getStringValue(caregiver, 'external_profile_note'),
        },
        {
            label: 'Agent instructions',
            value: getStringValue(caregiver, 'agent_instructions'),
        },
        {
            label: 'Notes',
            value: getStringValue(caregiver, 'notes'),
        },
    ].filter(
        (item): item is { readonly label: string; readonly value: string } =>
            item.value !== null,
    );

    if (contextItems.length === 0) {
        return null;
    }

    return (
        <div className="mt-3 space-y-1">
            <p className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
                Profile context
            </p>
            <div className="space-y-2">
                {contextItems.map((item) => (
                    <div
                        key={item.label}
                        className="border-border/60 bg-background rounded-md border px-2 py-1.5"
                    >
                        <p className="text-muted-foreground text-[10px] font-medium">
                            {item.label}
                        </p>
                        <p className="text-foreground mt-0.5 text-xs">
                            {item.value}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function CaregiverMemoryLookupCards({
    data,
    onCaregiverClick,
}: {
    readonly data: Record<string, unknown>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const [showAllCaregivers, setShowAllCaregivers] = useState(false);
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title="Caregiver memory unavailable"
                error={error}
            />
        );
    }

    const results = Array.isArray(data['results'])
        ? data['results'].filter(isRecord)
        : [];
    if (results.length === 0) {
        return (
            <p className="text-muted-foreground text-xs">
                No caregiver memory returned.
            </p>
        );
    }
    const visibleResults = showAllCaregivers
        ? results
        : results.slice(0, CAREGIVER_MEMORY_CARD_LIMIT);
    const hiddenCaregiverCount = results.length - visibleResults.length;
    return (
        <div className="space-y-2">
            {visibleResults.map((caregiver) => {
                const caregiverId = getStringValue(caregiver, 'caregiver_id');
                const caregiverName =
                    getCaregiverNameFromArgs(args, caregiverId) ?? 'Caregiver';
                const compiledMarkdown = getStringValue(
                    caregiver,
                    'compiled_markdown',
                );
                const hasMemory = caregiver['has_memory'] === true;
                const skills = getStringArray(caregiver['skills']);
                const tags = getStringArray(caregiver['tags']);
                const roles = getStringArray(caregiver['roles']);
                const hasSignalFields =
                    roles.length > 0 || skills.length > 0 || tags.length > 0;
                const matcher = buildCaregiverSignalMatcher(
                    args,
                    data['query'],
                    ...getCaregiverMatchSources(caregiver),
                );

                return (
                    <div
                        key={caregiverId ?? caregiverName}
                        className={cn(
                            'border-border/60 bg-card rounded-lg border p-3',
                            TOOL_SURFACE_HOVER_CLASS,
                        )}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <CaregiverNameButton
                                    caregiverId={caregiverId}
                                    name={caregiverName}
                                    onCaregiverClick={onCaregiverClick}
                                    className="text-sm font-semibold"
                                />
                                <p className="text-muted-foreground mt-0.5 text-[11px]">
                                    Caregiver memory and profile context
                                </p>
                            </div>
                        </div>
                        {hasMemory && compiledMarkdown ? (
                            <div className="mt-3">
                                <p className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
                                    What Phoebe remembers
                                </p>
                                <div className="mt-1 border-l-2 border-sky-300 bg-sky-50/60 px-2 py-1.5 dark:border-sky-800 dark:bg-sky-950/30">
                                    <Markdown className="text-foreground text-xs">
                                        {compiledMarkdown}
                                    </Markdown>
                                </div>
                            </div>
                        ) : null}
                        {!hasMemory ? (
                            <CaregiverProfileContextSection
                                caregiver={caregiver}
                            />
                        ) : null}
                        {hasSignalFields ? (
                            <div className="mt-3 space-y-2">
                                <CaregiverMemorySignalSection
                                    label="Roles"
                                    values={roles}
                                    matcher={matcher}
                                />
                                <CaregiverMemorySignalSection
                                    label="Skills"
                                    values={skills}
                                    matcher={matcher}
                                />
                                <CaregiverMemorySignalSection
                                    label="Notes"
                                    values={tags}
                                    matcher={matcher}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            })}
            {hiddenCaregiverCount > 0 ? (
                <button
                    type="button"
                    onClick={() => setShowAllCaregivers(true)}
                    className="text-primary hover:text-primary/80 cursor-pointer text-left text-[10px] font-medium"
                >
                    +{hiddenCaregiverCount} more caregivers
                </button>
            ) : null}
        </div>
    );
}

export function CaregiverMemorySearchCards({
    data,
    onCaregiverClick,
}: {
    readonly data: Record<string, unknown>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const [showAllCaregivers, setShowAllCaregivers] = useState(false);
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title="Caregiver search needs review"
                error={error}
            />
        );
    }

    const results = Array.isArray(data['results'])
        ? data['results'].filter(isRecord)
        : [];
    const count =
        typeof data['count'] === 'number' ? data['count'] : results.length;
    const query = getStringValue(data, 'query');
    const visibleResults = showAllCaregivers
        ? results
        : results.slice(0, CAREGIVER_MEMORY_CARD_LIMIT);
    const hiddenCaregiverCount = results.length - visibleResults.length;

    return (
        <div className="space-y-2">
            <div className="border-border/60 bg-muted/30 rounded-lg border px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-foreground text-sm font-semibold">
                        {count === 0
                            ? 'No caregivers found'
                            : `${count} caregiver${count === 1 ? '' : 's'} found`}
                    </p>
                    {query ? <ToolToken tone="blue">{query}</ToolToken> : null}
                </div>
            </div>
            {visibleResults.map((caregiver) => {
                const caregiverId = getStringValue(caregiver, 'caregiver_id');
                const caregiverName =
                    getStringValue(caregiver, 'name') ?? 'Caregiver';
                const memory = getStringValue(caregiver, 'memory');
                const agentInstructions = getStringValue(
                    caregiver,
                    'agent_instructions',
                );
                const roles = getStringArray(caregiver['roles']);
                const skills = getStringArray(caregiver['skills']);
                const tags = getStringArray(caregiver['tags']);
                const hasSignalFields =
                    roles.length > 0 || skills.length > 0 || tags.length > 0;
                const officeName = getStringValue(caregiver, 'office_name');
                const isActive = caregiver['is_active'] === true;
                const matcher = buildCaregiverSignalMatcher(
                    args,
                    query,
                    ...getCaregiverMatchSources(caregiver),
                );

                return (
                    <div
                        key={caregiverId ?? caregiverName}
                        className={cn(
                            'border-border/60 bg-card rounded-lg border p-3',
                            TOOL_SURFACE_HOVER_CLASS,
                        )}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <CaregiverNameButton
                                    caregiverId={caregiverId}
                                    name={caregiverName}
                                    onCaregiverClick={onCaregiverClick}
                                    className="text-sm font-semibold"
                                />
                                {officeName ? (
                                    <p className="text-muted-foreground mt-0.5 text-[11px]">
                                        {officeName}
                                    </p>
                                ) : null}
                            </div>
                            <ToolToken tone={isActive ? 'green' : 'neutral'}>
                                {isActive ? 'Active' : 'Inactive'}
                            </ToolToken>
                        </div>
                        <div className="mt-3 space-y-2">
                            {memory ? (
                                <ToolTopicSection title="Memory">
                                    <p className="text-foreground text-xs">
                                        {memory}
                                    </p>
                                </ToolTopicSection>
                            ) : null}
                            {agentInstructions ? (
                                <ToolTopicSection title="Instructions">
                                    <p className="text-foreground line-clamp-3 text-xs">
                                        {agentInstructions}
                                    </p>
                                </ToolTopicSection>
                            ) : null}
                            {hasSignalFields ? (
                                <Fragment>
                                    <CaregiverMemorySignalSection
                                        label="Roles"
                                        values={roles}
                                        matcher={matcher}
                                    />
                                    <CaregiverMemorySignalSection
                                        label="Skills"
                                        values={skills}
                                        matcher={matcher}
                                    />
                                    <CaregiverMemorySignalSection
                                        label="Notes"
                                        values={tags}
                                        matcher={matcher}
                                    />
                                </Fragment>
                            ) : null}
                        </div>
                    </div>
                );
            })}
            {hiddenCaregiverCount > 0 ? (
                <button
                    type="button"
                    onClick={() => setShowAllCaregivers(true)}
                    className="text-primary hover:text-primary/80 cursor-pointer text-left text-[10px] font-medium"
                >
                    +{hiddenCaregiverCount} more caregivers
                </button>
            ) : null}
        </div>
    );
}
